import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Popover } from "@mui/material";
import {
  TbAdjustmentsHorizontal,
  TbArrowDownRight,
  TbArrowUpRight,
  TbCalendar,
  TbCategory,
  TbChevronDown,
  TbCurrencyReal,
  TbDownload,
  TbSearch,
  TbSwitchHorizontal,
  TbTrash,
  TbX,
} from "react-icons/tb";
import { ValutField, ValutInput, ValutSelect } from "../../../Components/Atoms/ValutInput";
import { FilterDialog } from "../../../Components/Molecules/Filters/FilterDialog";
import {
  DateRangeCalendar,
  formatDateRangeLabel,
  GridFilterCheckboxOption,
  toggleFilterListValue,
} from "../../../Components/Molecules/GridHistory";
import { useToast } from "../../../Hooks/toast";
import {
  EMPTY_EXTRACT_FILTERS,
  buildExtractFilterTags,
  exportExtractCsv,
  getExtractTypeOptions,
  hasActiveExtractFilters,
  isExtractChipFilterActive,
  removeExtractFilterTag,
  resolveExtractCategoryId,
} from "./extractFilters";
import { ExtractViewTabs } from "./ExtractViewTabs";

const FILTER_CHIP_ICONS = {
  date: TbCalendar,
  amount: TbCurrencyReal,
  type: TbSwitchHorizontal,
  category: TbCategory,
  subAccount: TbAdjustmentsHorizontal,
  search: TbSearch,
};

const TYPE_OPTION_ICONS = {
  credit: TbArrowUpRight,
  debit: TbArrowDownRight,
};

const FilterDropdownButton = ({ icon: Icon, label, active, onClick }) => (
  <button
    type="button"
    className={`extract-filter-btn ${active ? "extract-filter-btn--active" : ""}`}
    onClick={onClick}
    aria-expanded={active}
  >
    <Icon className="extract-filter-btn__icon" aria-hidden />
    <span className="extract-filter-btn__label">{label}</span>
    <TbChevronDown className="extract-filter-btn__chevron" aria-hidden />
  </button>
);

const PanelActions = ({ onCancel, onApply }) => (
  <div className="extract-filter-panel__actions">
    <button type="button" className="extract-filter-panel__btn" onClick={onCancel}>
      {intl.get("common.cancel")}
    </button>
    <button
      type="button"
      className="extract-filter-panel__btn extract-filter-panel__btn--primary"
      onClick={onApply}
    >
      {intl.get("common.apply")}
    </button>
  </div>
);

const TypeFilterPanel = ({ draft, onChange }) => {
  const typeOptions = getExtractTypeOptions();
  const selectedCount = draft.types.length;

  const handleClear = () => onChange({ ...draft, types: [] });

  return (
    <div className="extract-filter-panel__body">
      {selectedCount > 0 && (
        <div className="extract-filter-panel__selection-bar">
          <span className="extract-filter-panel__selection-count">
            {intl.get("extract.selectedCount", { count: selectedCount })}
          </span>
          <button type="button" className="extract-filter-panel__clear-link" onClick={handleClear}>
            {intl.get("words.clear")}
          </button>
        </div>
      )}

      <div className="extract-filter-panel__options">
        {typeOptions.map((type) => {
          const TypeIcon = TYPE_OPTION_ICONS[type.id] || TbSwitchHorizontal;
          return (
            <label
              key={type.id}
              htmlFor={`extract-type-${type.id}`}
              className="extract-filter-option"
            >
              <input
                id={`extract-type-${type.id}`}
                type="checkbox"
                checked={draft.types.includes(type.id)}
                onChange={() =>
                  onChange({
                    ...draft,
                    types: toggleFilterListValue(draft.types, type.id),
                  })
                }
                className="extract-filter-option__checkbox"
              />
              <span className={`extract-filter-option__icon extract-filter-option__icon--${type.id}`}>
                <TypeIcon aria-hidden />
              </span>
              <span className="extract-filter-option__label">{type.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const CategoryFilterPanel = ({ draft, onChange, categoryOptions = [], loading = false }) => {
  const selected = draft.categories || [];
  const selectedCount = selected.length;

  const handleClear = () => onChange({ ...draft, categories: [] });

  return (
    <div className="extract-filter-panel__body">
      {selectedCount > 0 && (
        <div className="extract-filter-panel__selection-bar">
          <span className="extract-filter-panel__selection-count">
            {intl.get("extract.selectedCount", { count: selectedCount })}
          </span>
          <button type="button" className="extract-filter-panel__clear-link" onClick={handleClear}>
            {intl.get("words.clear")}
          </button>
        </div>
      )}

      <div className="extract-filter-panel__options">
        {loading && categoryOptions.length === 0 ? (
          <p className="extract-filter-panel__empty">{intl.get("extract.categoriesLoading")}</p>
        ) : null}
        {!loading && categoryOptions.length === 0 ? (
          <p className="extract-filter-panel__empty">{intl.get("common.noRecordsFiltered")}</p>
        ) : null}
        {categoryOptions.map((category) => {
          const categoryId = resolveExtractCategoryId(category.id);
          if (categoryId == null) return null;
          return (
            <label
              key={categoryId}
              htmlFor={`extract-category-${categoryId}`}
              className="extract-filter-option"
            >
              <input
                id={`extract-category-${categoryId}`}
                type="checkbox"
                checked={selected.some((value) => resolveExtractCategoryId(value) === categoryId)}
                onChange={() =>
                  onChange({
                    ...draft,
                    categories: toggleFilterListValue(selected, categoryId),
                  })
                }
                className="extract-filter-option__checkbox"
              />
              <span className="extract-filter-option__label">
                {category.label || category.name}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const DateFilterPanel = ({ draft, onChange }) => (
  <div className="extract-filter-panel__body">
    <DateRangeCalendar
      dateFrom={draft.dateFrom}
      dateTo={draft.dateTo}
      onChange={({ dateFrom, dateTo }) => onChange({ ...draft, dateFrom, dateTo })}
    />
  </div>
);

const AmountFilterPanel = ({ draft, onChange }) => (
  <div className="extract-filter-panel__body">
    <div className="grid gap-3 sm:grid-cols-2">
      <ValutField label={intl.get("extract.amountMin")} htmlFor="extract-filter-amount-min">
        <ValutInput
          id="extract-filter-amount-min"
          inputMode="decimal"
          placeholder="0,00"
          value={draft.amountMin}
          onChange={(event) => onChange({ ...draft, amountMin: event.target.value })}
        />
      </ValutField>
      <ValutField label={intl.get("extract.amountMax")} htmlFor="extract-filter-amount-max">
        <ValutInput
          id="extract-filter-amount-max"
          inputMode="decimal"
          placeholder="0,00"
          value={draft.amountMax}
          onChange={(event) => onChange({ ...draft, amountMax: event.target.value })}
        />
      </ValutField>
    </div>
  </div>
);

const MoreFiltersForm = ({
  draft,
  onChange,
  subAccounts = [],
  categoryOptions = [],
  categoriesLoading = false,
}) => (
  <div className="extract-more-filters-form">
    <section className="extract-more-filters-form__section extract-more-filters-form__section--date">
      <p className="extract-more-filters-form__label">{intl.get("extract.filterDate")}</p>
      <DateRangeCalendar
        variant="modal"
        showDateInputs
        dateFrom={draft.dateFrom}
        dateTo={draft.dateTo}
        onChange={({ dateFrom, dateTo }) => onChange({ ...draft, dateFrom, dateTo })}
      />
    </section>

    <section className="extract-more-filters-form__section">
      <p className="extract-more-filters-form__label">{intl.get("extract.filterAmount")}</p>
      <div className="extract-more-filters-form__amount-grid">
        <ValutField label={intl.get("extract.amountMin")} htmlFor="extract-more-amount-min">
          <ValutInput
            id="extract-more-amount-min"
            inputMode="decimal"
            placeholder="0,00"
            value={draft.amountMin}
            onChange={(event) => onChange({ ...draft, amountMin: event.target.value })}
          />
        </ValutField>
        <ValutField label={intl.get("extract.amountMax")} htmlFor="extract-more-amount-max">
          <ValutInput
            id="extract-more-amount-max"
            inputMode="decimal"
            placeholder="0,00"
            value={draft.amountMax}
            onChange={(event) => onChange({ ...draft, amountMax: event.target.value })}
          />
        </ValutField>
      </div>
    </section>

    <section className="extract-more-filters-form__section">
      <p className="extract-more-filters-form__label">{intl.get("extract.filterType")}</p>
      <div className="extract-more-filters-form__type-options">
        {getExtractTypeOptions().map((type) => (
          <GridFilterCheckboxOption
            key={type.id}
            id={`extract-more-type-${type.id}`}
            label={type.label}
            checked={draft.types.includes(type.id)}
            onChange={() =>
              onChange({
                ...draft,
                types: toggleFilterListValue(draft.types, type.id),
              })
            }
          />
        ))}
      </div>
    </section>

    <section className="extract-more-filters-form__section">
      <p className="extract-more-filters-form__label">{intl.get("extract.filterCategory")}</p>
      <div className="extract-more-filters-form__type-options">
        {categoriesLoading && categoryOptions.length === 0 ? (
          <p className="extract-filter-panel__empty">{intl.get("extract.categoriesLoading")}</p>
        ) : null}
        {categoryOptions.map((category) => {
          const categoryId = resolveExtractCategoryId(category.id);
          if (categoryId == null) return null;
          return (
            <GridFilterCheckboxOption
              key={categoryId}
              id={`extract-more-category-${categoryId}`}
              label={category.label || category.name}
              checked={(draft.categories || []).some(
                (value) => resolveExtractCategoryId(value) === categoryId,
              )}
              onChange={() =>
                onChange({
                  ...draft,
                  categories: toggleFilterListValue(draft.categories || [], categoryId),
                })
              }
            />
          );
        })}
      </div>
    </section>

    {Array.isArray(subAccounts) && subAccounts.length >= 1 && (
      <section className="extract-more-filters-form__section">
        <ValutField label={intl.get("extract.subAccount")} htmlFor="extract-more-sub-account">
          <ValutSelect
            id="extract-more-sub-account"
            value={draft.subAccountId}
            onChange={(event) => onChange({ ...draft, subAccountId: event.target.value })}
          >
            <option value="">{intl.get("common.all")}</option>
            {subAccounts.map((sa) => (
              <option key={sa.subAccountId} value={sa.subAccountId}>
                {sa.name}
              </option>
            ))}
          </ValutSelect>
        </ValutField>
      </section>
    )}
  </div>
);

export const ExtractModernFilterBar = ({
  filters,
  onFiltersChange,
  filteredItems,
  subAccounts = [],
  categoryOptions = [],
  categoriesLoading = false,
  onExportClick,
  activeTab,
  onTabChange,
  showFilters = true,
  leadingActions = null,
}) => {
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [draft, setDraft] = useState(filters);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.searchQuery ?? "");

  useEffect(() => {
    setSearchValue(filters.searchQuery ?? "");
  }, [filters.searchQuery]);

  const appliedTags = useMemo(
    () => buildExtractFilterTags(filters, subAccounts, categoryOptions),
    [filters, subAccounts, categoryOptions],
  );
  const filtersActive = hasActiveExtractFilters(filters);

  const closePanel = () => {
    setAnchorEl(null);
    setActivePanel(null);
  };

  const openMoreFilters = () => {
    closePanel();
    setDraft({ ...filters, categories: filters.categories || [] });
    setMoreFiltersOpen(true);
  };

  const openPanel = (event, panelId) => {
    if (panelId === "more") {
      openMoreFilters();
      return;
    }
    setDraft({ ...filters, categories: filters.categories || [] });
    setActivePanel(panelId);
    setAnchorEl(event.currentTarget);
  };

  const applyDraft = () => {
    onFiltersChange(draft);
    closePanel();
    setMoreFiltersOpen(false);
  };

  const handleClearAll = () => {
    onFiltersChange(EMPTY_EXTRACT_FILTERS);
    setDraft(EMPTY_EXTRACT_FILTERS);
    setSearchValue("");
    closePanel();
    setMoreFiltersOpen(false);
  };

  const handleRemoveTag = (tagKey) => {
    onFiltersChange(removeExtractFilterTag(filters, tagKey));
  };

  const handleChipClick = (event) => {
    event.stopPropagation();
    openMoreFilters();
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    onFiltersChange({ ...filters, searchQuery: value });
  };

  const handleExport = () => {
    if (onExportClick) {
      onExportClick();
      return;
    }

    if (filteredItems.length === 0) {
      toast.addToast({
        type: "info",
        title: intl.get("common.nothingToExport"),
        description: intl.get("common.noRecordsFiltered"),
      });
      return;
    }

    exportExtractCsv(filteredItems);
    toast.addToast({
      type: "success",
      title: intl.get("common.exportComplete"),
      description: intl.get("common.exportCompleteCsv", { count: filteredItems.length }),
    });
  };

  const panelTitles = {
    date: intl.get("extract.filterDate"),
    amount: intl.get("extract.filterAmount"),
    type: intl.get("extract.filterType"),
    category: intl.get("extract.filterCategory"),
  };

  const dateChipActive = isExtractChipFilterActive(filters, "date");
  const dateChipLabel = dateChipActive
    ? formatDateRangeLabel(filters?.dateFrom, filters?.dateTo) || intl.get("extract.filterDate")
    : intl.get("extract.filterDate");

  return (
    <div className="extract-modern-filter">
      <div className="extract-modern-filter__toolbar">
        {showFilters ? (
          <div className="extract-modern-filter__row">
            <div className="extract-modern-filter__search">
              <TbSearch className="extract-modern-filter__search-icon" aria-hidden />
              <input
                type="search"
                className="extract-modern-filter__search-input"
                placeholder={intl.get("extract.searchTransaction")}
                value={searchValue}
                onChange={handleSearchChange}
                aria-label={intl.get("extract.searchTransaction")}
              />
            </div>

            <div className="extract-modern-filter__buttons">
              <FilterDropdownButton
                icon={TbCalendar}
                label={dateChipLabel}
                active={dateChipActive}
                onClick={(event) => openPanel(event, "date")}
              />
              <FilterDropdownButton
                icon={TbCurrencyReal}
                label={intl.get("extract.filterAmount")}
                active={isExtractChipFilterActive(filters, "amount")}
                onClick={(event) => openPanel(event, "amount")}
              />
              <FilterDropdownButton
                icon={TbSwitchHorizontal}
                label={intl.get("extract.filterType")}
                active={isExtractChipFilterActive(filters, "type")}
                onClick={(event) => openPanel(event, "type")}
              />
              <FilterDropdownButton
                icon={TbCategory}
                label={intl.get("extract.filterCategory")}
                active={isExtractChipFilterActive(filters, "category")}
                onClick={(event) => openPanel(event, "category")}
              />
              <FilterDropdownButton
                icon={TbAdjustmentsHorizontal}
                label={intl.get("common.moreFilters")}
                active={
                  isExtractChipFilterActive(filters, "subAccount")
                  || isExtractChipFilterActive(filters, "category")
                }
                onClick={(event) => openPanel(event, "more")}
              />
            </div>
          </div>
        ) : null}

        <div className="extract-modern-filter__actions">
          {activeTab != null && onTabChange ? (
            <ExtractViewTabs activeTab={activeTab} onTabChange={onTabChange} inline />
          ) : null}
          {leadingActions}
          <button type="button" className="grid-history-action-btn" onClick={handleExport}>
            <TbDownload className="h-4 w-4" aria-hidden />
            {intl.get("common.export")}
          </button>
        </div>
      </div>

      {showFilters && (filtersActive || appliedTags.length > 0) && (
        <div className="extract-modern-filter__active-row">
          <span className="extract-modern-filter__active-label">
            {intl.get("extract.activeFilters")}
          </span>
          <div className="extract-modern-filter__active-chips">
            {appliedTags.map((tag) => {
              const TagIcon = FILTER_CHIP_ICONS[tag.iconType] || TbAdjustmentsHorizontal;
              return (
                <button
                  key={tag.key}
                  type="button"
                  className={`extract-active-chip extract-active-chip--${tag.iconType}`}
                  onClick={handleChipClick}
                >
                  <span className="extract-active-chip__icon">
                    <TagIcon aria-hidden />
                  </span>
                  <span className="extract-active-chip__value">{tag.value}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="extract-active-chip__remove"
                    aria-label={intl.get("words.clear")}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveTag(tag.key);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        handleRemoveTag(tag.key);
                      }
                    }}
                  >
                    <TbX aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="extract-modern-filter__clear-all"
            onClick={handleClearAll}
          >
            <TbTrash aria-hidden />
            {intl.get("extract.clearAll")}
          </button>
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePanel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: `extract-filter-panel${
            activePanel === "date" ? " extract-filter-panel--date" : ""
          }${activePanel === "category" ? " extract-filter-panel--category" : ""}`.trim(),
          sx: {
            mt: 1,
            width: {
              xs: activePanel === "date" ? "min(300px, calc(100vw - 2rem))" : "calc(100vw - 2rem)",
              sm: activePanel === "date" ? "auto" : 320,
            },
            maxWidth:
              activePanel === "date"
                ? { xs: "calc(100vw - 2rem)", sm: "min(560px, calc(100vw - 2rem))" }
                : "calc(100vw - 2rem)",
            maxHeight: activePanel === "category" ? "min(420px, 70vh)" : undefined,
          },
        }}
      >
        {activePanel ? (
          <>
            <p className="extract-filter-panel__title">{panelTitles[activePanel]}</p>
            {activePanel === "date" && <DateFilterPanel draft={draft} onChange={setDraft} />}
            {activePanel === "amount" && <AmountFilterPanel draft={draft} onChange={setDraft} />}
            {activePanel === "type" && <TypeFilterPanel draft={draft} onChange={setDraft} />}
            {activePanel === "category" && (
              <CategoryFilterPanel
                draft={draft}
                onChange={setDraft}
                categoryOptions={categoryOptions}
                loading={categoriesLoading}
              />
            )}
            <PanelActions onCancel={closePanel} onApply={applyDraft} />
          </>
        ) : null}
      </Popover>

      <FilterDialog
        className="filter-dialog--extract-more"
        open={moreFiltersOpen}
        onClose={() => setMoreFiltersOpen(false)}
        footer={(
          <>
            <button type="button" className="grid-history-action-btn" onClick={handleClearAll}>
              {intl.get("words.clear")}
            </button>
            <button
              type="button"
              className="grid-history-action-btn grid-history-action-btn--primary"
              onClick={applyDraft}
            >
              {intl.get("common.filter")}
            </button>
          </>
        )}
      >
        <MoreFiltersForm
          draft={draft}
          onChange={setDraft}
          subAccounts={subAccounts}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
        />
      </FilterDialog>
    </div>
  );
};
